import { catalog } from '../core/catalog.js';
import { openReport } from './report.js';
import { openMatch } from './match.js';
import { stripCountry } from '../core/country.js';
import { t, i18n } from '../core/i18n.js';
import { openExternal } from '../core/external.js';
import { playSoundtrack } from '../ui/spotify.js';
import { device } from '../core/device.js';
import { escapeHtml, formatDuration, formatMinutes, formatDate, splitCast } from '../core/utils.js';
import { parseAge } from '../core/age.js';
import { movieCard, seriesCard, row, enhance, empty, imageUrl } from '../ui/render.js';
import { HeroPreview } from '../ui/preview.js';
import { runSpeedTest } from '../core/speedtest.js';
import { previewLink } from '../core/share.js';
import { codecSupport, CODEC_LABELS } from '../core/codecs.js';
import { transcodeAvailable } from '../core/transcode.js';
import { offlineButton, offlineKey, offlineAction, progressText } from '../ui/offline-ui.js';
import { offline } from '../core/offline.js';
import { account } from '../core/account.js';
import { notify } from '../core/notify.js';
import { genreList } from '../core/genres.js';
import { publicUrl, urlSyncEnabled } from '../core/deeplink.js';
import { openMenu } from '../ui/menu.js';
import { openEditItem } from './edit-item.js';
import { itemEdits } from '../core/item-edits.js';
import { settings } from '../core/storage.js';

/* Prueba de velocidad de la película: proveedor → servidor y servidor → dispositivo. */
function mbps(kbps) {
    return (kbps / 1000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' Mb/s';
}

function legLine(label, leg) {
    const value = !leg ? t('speed.unknown') : leg.error ? t('speed.unknown') + ' (' + escapeHtml(String(leg.error)) + ')' : mbps(leg.kbps);
    return `<div class="speed-line"><span>${label}</span><b>${value}</b></div>`;
}

function speedReport(r) {
    const lines = [];
    if (r.usesProxy) {
        lines.push(legLine(t('speed.serverLeg'), r.server));
        lines.push(legLine(t('speed.deviceLeg'), r.device));
    } else {
        lines.push(legLine(t('speed.directLeg'), r.device));
    }
    lines.push(`<div class="speed-line"><span>${t('speed.needed')}</span><b>${r.needKbps ? '~' + mbps(r.needKbps) : t('speed.unknown')}</b></div>`);
    let verdict = t('speed.hint');
    const serverKbps = r.server && !r.server.error ? r.server.kbps : Infinity;
    const deviceKbps = r.device && !r.device.error ? r.device.kbps : Infinity;
    if (r.needKbps && isFinite(Math.min(serverKbps, deviceKbps))) {
        const need = r.needKbps * 1.15;
        if (r.usesProxy && serverKbps < need && serverKbps <= deviceKbps) verdict = t('speed.slowServer');
        else if (deviceKbps < need) verdict = t(r.usesProxy ? 'speed.slowDevice' : 'speed.slowDirect');
        else verdict = t('speed.ok');
    }
    return `<div class="speed-report">${lines.join('')}</div><p class="speed-verdict">${verdict}</p>`;
}

async function openSpeedTest(info) {
    const app = window.app;
    const progress = app.dialog.preloader(t('speed.testingServer'));
    let result;
    try {
        result = await runSpeedTest({
            rawUrl: catalog.rawVodUrl(info),
            playUrl: catalog.vodUrl(info),
            durationSecs: info.durationSecs,
            bitrateKbps: Number(info.bitrate) || 0,
            onStep: (step) => progress.setText(step === 'server' ? t('speed.testingServer') : t('speed.testingDevice'))
        });
    } finally {
        progress.close();
    }
    app.dialog.alert(speedReport(result), t('speed.title'));
}

/* Segundo por el que arranca la vista previa: pasado el arranque/créditos iniciales. */
function previewStart(durationSecs) {
    if (!durationSecs || durationSecs < 300) return 0;
    return Math.min(Math.floor(durationSecs * 0.08), 600);
}

function bindPreview(root, preview) {
    preview.schedule();
    const player = window.IPTV && window.IPTV.player;
    const offOpen = player ? player.on('open', () => preview.stop()) : () => {};
    const onVisibility = () => { if (document.hidden) preview.stop(); };
    document.addEventListener('visibilitychange', onVisibility);
    // La vista previa es otra conexión al proveedor: al empezar una descarga se cierra, porque muchos solo dejan una a la vez.
    const offDownload = offline.on('change', (rec) => { if (rec && rec.status === 'downloading') preview.stop(); });
    // Al pasar a otra pantalla la ficha no se destruye (queda debajo): su vista previa tiene que callar ya.
    // Al volver a ella, arranca de nuevo.
    const onOut = () => preview.stop();
    const onIn = () => preview.schedule();
    root.addEventListener('page:beforeout', onOut);
    root.addEventListener('page:afterin', onIn);
    return () => {
        offOpen();
        offDownload();
        document.removeEventListener('visibilitychange', onVisibility);
        root.removeEventListener('page:beforeout', onOut);
        root.removeEventListener('page:afterin', onIn);
        preview.destroy();
    };
}

/* Fichas de película y de serie. Cabecera tipo hero + acciones + sinopsis + episodios/similares. */

function backButton(kind) {
    return `<button class="detail-back" data-back aria-label="${t('common.back')}"><span class="btn btn-icon"><span class="mi">arrow_back</span></span><span class="detail-back-label">${kind === 'vod' ? t('nav.movies') : t('nav.series')}</span></button>`;
}

function techFacts(info) {
    if (!info.video && !info.audio) return [];
    const sup = codecSupport(info);
    const vParts = [];
    if (sup.video) vParts.push(CODEC_LABELS[sup.video] || sup.video.toUpperCase());
    if (info.video && info.video.width && info.video.height) vParts.push(info.video.width + '×' + info.video.height);
    const aParts = [];
    if (sup.audio) aParts.push(CODEC_LABELS[sup.audio] || sup.audio.toUpperCase());
    if (info.audio && info.audio.channels) aParts.push(info.audio.channels === 6 ? '5.1' : info.audio.channels === 8 ? '7.1' : info.audio.channels === 2 ? 'estéreo' : info.audio.channels + ' ch');
    const out = [];
    if (vParts.length) out.push([t('detail.video'), vParts.join(' · ') + (sup.videoOk ? '' : ' ⚠')]);
    if (aParts.length) out.push([t('detail.audio'), aParts.join(' · ') + (sup.audioOk ? '' : transcodeAvailable() ? ' → AAC' : ' ⚠')]);
    return out;
}

/** `notes`: incluye también los avisos que solo informan (audio que el servidor ya convierte). */
function compatWarning(info, { notes = false } = {}) {
    if (!info.video && !info.audio) return '';
    const sup = codecSupport(info);
    if (sup.videoOk && sup.audioOk) return '';
    // Con conversión de audio en el servidor, el audio Dolby/DTS ya no es un problema.
    if (sup.videoOk && !sup.audioOk && transcodeAvailable()) {
        return notes ? `<div class="detail-warn is-info"><span class="mi">graphic_eq</span><span>${t('detail.compatAudioFixed')}</span></div>` : '';
    }
    const what = !sup.audioOk && !sup.videoOk ? t('detail.compatBoth') : !sup.audioOk ? t('detail.compatAudio') : t('detail.compatVideo');
    return `<div class="detail-warn"><span class="mi">warning</span><span>${what}</span></div>`;
}

/** Reparto como chips: cada nombre abre su página con las películas y series en las que aparece. */
/** Línea de crédito bajo una sinopsis que viene de Wikipedia (CC BY-SA 4.0): obligatoria al reutilizar el texto. */
function plotCreditHtml(info) {
    const src = info && info.plotSource;
    if (!src) return '';
    const text = escapeHtml(t('detail.plotCredit'));
    const link = src.url ? `<a class="external" href="${escapeHtml(src.url)}" target="_blank" rel="noopener">${text}</a>` : text;
    const lic = src.licenseUrl ? ` · <a class="external" href="${escapeHtml(src.licenseUrl)}" target="_blank" rel="noopener">CC BY-SA</a>` : '';
    return `<p class="plot-credit">${link}${lic}</p>`;
}

function castHtml(cast, castList, { mode = 'toggle' } = {}) {
    const list = Array.isArray(castList) && castList.length ? castList : splitCast(cast).map((name) => ({ name }));
    if (!list.length) return '';
    const chip = (c) => `<button type="button" class="cast-chip ${c.photo ? 'has-photo' : ''}" data-actor="${escapeHtml(c.name)}" title="${escapeHtml(c.character || '')}">${c.photo ? `<img data-src="${escapeHtml(imageUrl(c.photo))}" alt=""/>` : ''}<span class="mi">person</span>${escapeHtml(c.name)}</button>`;
    // Cabecera: solo los cinco primeros; el resto está en «Más información».
    if (mode === 'compact') return `<span class="cast-list is-expanded" data-nav-row data-nav-reach>${list.slice(0, 5).map(chip).join('')}</span>`;
    // Diálogo: todos los nombres y, si TMDB trajo fotos, el carrusel de caras a la vista.
    if (mode === 'all') {
        const faces = list.some((c) => c.photo) ? `<div class="cast-carousel" data-nav-row>${list.map((c) => `<button type="button" class="cast-face" data-actor="${escapeHtml(c.name)}"><span class="cast-face-img">${c.photo ? `<img data-src="${escapeHtml(imageUrl(c.photo))}" alt=""/>` : ''}<span class="mi">person</span></span><b>${escapeHtml(c.name)}</b>${c.character ? `<small>${escapeHtml(c.character)}</small>` : ''}</button>`).join('')}</div>` : '';
        return faces || `<span class="cast-list is-expanded" data-nav-row>${list.map(chip).join('')}</span>`;
    }
    // Los cinco primeros a la vista; el resto tras «+N más» (se puede volver a plegar).
    const VISIBLE = 5;
    const extra = Math.max(0, list.length - VISIBLE);
    // Al pulsar «+N más» (o «Ver más» si el reparto es corto) aparece además un carrusel con las caras de todo el reparto (solo si TMDB trajo fotos).
    const faces = list.some((c) => c.photo)
        ? `<div class="cast-carousel" data-nav-row hidden>${list.map((c) => `<button type="button" class="cast-face" data-actor="${escapeHtml(c.name)}"><span class="cast-face-img">${c.photo ? `<img data-src="${escapeHtml(imageUrl(c.photo))}" alt=""/>` : ''}<span class="mi">person</span></span><b>${escapeHtml(c.name)}</b>${c.character ? `<small>${escapeHtml(c.character)}</small>` : ''}</button>`).join('')}</div>`
        : '';
    return `<span class="cast-list" data-nav-row>${list.map((c, i) => `<button type="button" class="cast-chip ${c.photo ? 'has-photo' : ''} ${i >= VISIBLE ? 'is-extra' : ''}" data-actor="${escapeHtml(c.name)}" title="${escapeHtml(c.character || '')}">${c.photo ? `<img data-src="${escapeHtml(imageUrl(c.photo))}" alt=""/>` : ''}<span class="mi">person</span>${escapeHtml(c.name)}</button>`).join('')}${extra || faces ? `<button type="button" class="cast-chip cast-more" data-cast-toggle data-label-more="${escapeHtml(extra ? t('detail.castMore', { n: extra }) : t('detail.castSeeMore'))}" data-label-less="${escapeHtml(t('detail.castLess'))}"><span class="mi">expand_more</span>${escapeHtml(extra ? t('detail.castMore', { n: extra }) : t('detail.castSeeMore'))}</button>` : ''}</span>` + faces;
}

const chips = (list) => `<span class="fact-chips">${list.map((x) => `<span class="fact-chip">${escapeHtml(x)}</span>`).join('')}</span>`;

function directorsHtml(info) {
    const directors = String(info.director || '').split(',').map((x) => x.trim()).filter(Boolean);
    return directors.length ? `<div class="detail-fact"><b>${t('common.director')}</b><span class="cast-list is-expanded" data-nav-row data-nav-reach>${directors.map((n) => `<button type="button" class="cast-chip" data-actor="${escapeHtml(n)}"><span class="mi">movie_creation</span>${escapeHtml(n)}</button>`).join('')}</span></div>` : '';
}

/** Búsqueda de la banda sonora en Spotify: título original (o el de TMDB) y el compositor si se conoce; si no, «soundtrack».
    Es un enlace de búsqueda, sin clave de Spotify: en Android y en la tele lo abre la app de Spotify si está instalada. */
function spotifyUrl(info) {
    const title = String(info.originalTitle || watchTitle(info) || '').trim();
    if (!title) return '';
    const composer = String(info.composer || '').split(',')[0].trim();
    return 'https://open.spotify.com/search/' + encodeURIComponent(title + ' ' + (composer || 'soundtrack'));
}

function musicHtml(info) {
    const composers = String(info.composer || '').split(',').map((x) => x.trim()).filter(Boolean);
    const url = spotifyUrl(info);
    if (!composers.length && !url) return '';
    const chips = composers.map((n) => `<button type="button" class="cast-chip" data-actor="${escapeHtml(n)}"><span class="mi">music_note</span>${escapeHtml(n)}</button>`).join('');
    const spotify = url ? `<button type="button" class="cast-chip is-spotify" data-spotify data-url="${escapeHtml(url)}"><span class="mi">play_circle</span>${escapeHtml(t('detail.spotify'))}</button>` : '';
    return `<div class="detail-fact"><b>${t('common.composer')}</b><span class="cast-list is-expanded" data-nav-row data-nav-reach>${chips}${spotify}</span></div>`;
}

/** Valoraciones de OMDb (IMDb, Rotten Tomatoes, Metacritic), si el servidor tiene su clave. */
function ratingsHtml(info) {
    if (settings.get('omdbOn') === false) return '';
    const n = (v) => escapeHtml(String(Math.round(v * 10) / 10).replace('.', ','));
    const list = [];
    if (info.imdbRating) list.push(`IMDb ★ ${n(info.imdbRating)}`);
    if (info.rt) list.push(`Rotten Tomatoes ${info.rt} %`);
    if (info.metascore) list.push(`Metacritic ${info.metascore}`);
    if (!list.length) return '';
    return `<div class="detail-fact"><b>${t('detail.ratings')}</b>${chips(list)}</div>`;
}

/** Enlaces a los servicios que han aportado datos a esta ficha: IMDb (si se conoce su identificador, venga de donde venga), TMDB (si
    TMDB la completó) y OMDb (si puso valoraciones). Solo son enlaces: no se pide nada a esos servicios. */
function linksHtml(info) {
    const imdb = String(info.imdb || '').trim();
    const kind = info.seasons ? 'tv' : 'movie';
    const imdbUrl = /^tt\d+$/.test(imdb) ? `https://www.imdb.com/title/${imdb}/` : '';
    // `srcTmdb`: lo marca el catálogo al completar con TMDB; las fichas guardadas antes se reconocen por `watch` (solo lo pone TMDB).
    const fromTmdb = !catalog._tmdbOff() && (info.srcTmdb || info.watch !== undefined) && /^\d+$/.test(String(info.tmdb || ''));
    const tmdbUrl = fromTmdb ? `https://www.themoviedb.org/${kind}/${info.tmdb}` : '';
    const fromOmdb = settings.get('omdbOn') !== false && !!(info.srcOmdb || info.imdbRating || info.rt || info.metascore);
    const chip = (url, label) => `<button type="button" class="cast-chip" data-ext="${escapeHtml(url)}"><span class="mi">open_in_new</span>${escapeHtml(label)}</button>`;
    const chips = [imdbUrl ? chip(imdbUrl, 'IMDb') : '', tmdbUrl ? chip(tmdbUrl, 'TMDB') : '', fromOmdb ? chip('https://www.omdbapi.com/', 'OMDb') : ''].filter(Boolean).join('');
    return chips ? `<div class="detail-fact"><b>${t('detail.links')}</b><span class="cast-list is-expanded" data-nav-row data-nav-reach>${chips}</span></div>` : '';
}

/* Datos a la vista en la ficha: lo que ayuda a decidir si verla (director, cinco del reparto y dónde está en suscripción).
   Todo lo demás (resto del reparto, alquiler, género, proveedor, estreno, formato, códecs) va tras «Más información»:
   la cabecera dejaba de verse (y de verse el tráiler) bajo tantas filas. */
function facts(info) {
    const rows = [];
    const dir = directorsHtml(info);
    if (dir) rows.push(dir);
    // Ajuste «Información de la ficha: Completa»: todo a la vista, como antes de aligerarla.
    if (settings.get('detailHeader') === 'full') {
        const cast = castHtml(info.cast, info.castList);
        if (cast) rows.push(`<div class="detail-fact ${cast.indexOf('cast-carousel') !== -1 ? 'has-cast-faces' : ''}"><b>${t('common.cast')}</b>${cast}</div>`);
        const musicFull = musicHtml(info);
        if (musicFull) rows.push(musicFull);
        const linksFull = linksHtml(info);
        if (linksFull) rows.push(linksFull);
        const ratingsFull = ratingsHtml(info);
        if (ratingsFull) rows.push(ratingsFull);
        watchHtml(info).forEach((r) => rows.push(r));
        const genres = genreList(info.genre);
        if (genres.length) rows.push(`<div class="detail-fact"><b>${t('common.genre')}</b>${chips(genres)}</div>`);
        const from = providerOf(info);
        if (from) rows.push(`<div class="detail-fact"><b>${t('detail.provider')}</b>${chips([from.own])}${from.others.length ? `<span class="fact-text">${escapeHtml(t('detail.alsoIn', { names: from.others.join(', ') }))}</span>` : ''}</div>`);
        const tail = [];
        if (info.releaseDate) tail.push(`<b>${t('common.released')}</b><span class="fact-text">${escapeHtml(formatDate(info.releaseDate))}</span>`);
        if (info.ext) tail.push(`<b>${t('common.format')}</b>${chips([String(info.ext).toUpperCase()])}`);
        if (tail.length) rows.push(`<div class="detail-fact is-inline">${tail.join('')}</div>`);
        return rows.length ? `<div class="detail-facts">${rows.join('')}</div>` : '';
    }
    const cast = castHtml(info.cast, info.castList, { mode: 'compact' });
    if (cast) rows.push(`<div class="detail-fact"><b>${t('common.cast')}</b>${cast}</div>`);
    const music = musicHtml(info);
    if (music) rows.push(music);
    // Enlaces (IMDb, TMDB, OMDb) y valoraciones no van en la ficha: se ven en «Información adicional» (y en la ficha «Completa»).
    watchHtml(info, { subscription: true }).forEach((r) => rows.push(r));
    return rows.length ? `<div class="detail-facts">${rows.join('')}</div>` : '';
}

/** De qué proveedor sale el título y en cuáles más está: { own, others }, o null si no se sabe. */
function providerOf(info) {
    const routes = catalog.playRoutes(catalog.get('vod', info.id) || catalog.get('series', info.id) || info);
    const own = routes.find((r) => r.order === 0);
    if (!own || !own.name) return null;
    return { own: own.name, others: routes.filter((r) => r.order !== 0 && r.name && r.name !== own.name).map((r) => r.name) };
}

function providerText(info) {
    const from = providerOf(info);
    return from ? from.own + (from.others.length ? ' · ' + t('detail.alsoIn', { names: from.others.join(', ') }) : '') : '';
}

/** Ventana «Información»: sinopsis completa y todos los datos, incluidos los técnicos. */
function openInfo(info) {
    const list = [
        [t('common.genre'), genreList(info.genre).join(', ')],
        [t('common.country'), info.country],
        [t('detail.awards'), info.awards],
        [t('common.released'), info.releaseDate ? formatDate(info.releaseDate) : info.year],
        [t('common.duration'), info.durationSecs ? formatMinutes(info.durationSecs) : ''],
        [t('common.format'), info.ext ? String(info.ext).toUpperCase() : ''],
        [t('detail.provider'), providerText(info)]
    ].concat(techFacts(info)).filter((f) => f[1]);
    const cast = castHtml(info.cast, info.castList, { mode: 'all' });
    const people = `${directorsHtml(info)}${cast ? `<div class="detail-fact ${cast.indexOf('cast-carousel') !== -1 ? 'has-cast-faces' : ''}"><b>${t('common.cast')}</b>${cast}</div>` : ''}${musicHtml(info)}${linksHtml(info)}${ratingsHtml(info)}${watchHtml(info).join('')}`;
    const html = `<div class="detail-info-sheet">
        ${info.plot ? `<p>${escapeHtml(info.plot)}</p>` : ''}
        ${info.plot ? plotCreditHtml(info) : ''}
        ${compatWarning(info, { notes: true })}
        ${people ? `<div class="detail-facts">${people}</div>` : ''}
        <dl>${list.map((f) => `<dt>${escapeHtml(f[0])}</dt><dd>${escapeHtml(String(f[1]))}</dd>`).join('')}</dl>
    </div>`;
    const dlg = window.app.dialog.create({ title: escapeHtml(stripCountry(info.name)), content: html, cssClass: 'detail-info-dialog', buttons: [{ text: t('common.close') }] });
    // Plataformas y nombres dentro del diálogo: el enlace se abre fuera; una persona cierra el diálogo y lleva a su página.
    dlg.el.addEventListener('click', (e) => {
        const sp = e.target.closest('[data-spotify]');
        if (sp) { e.preventDefault(); e.stopPropagation(); playSoundtrack(info, { fallbackUrl: sp.getAttribute('data-url') || '' }); return; }
        const ext = e.target.closest('[data-ext]');
        if (ext) { e.preventDefault(); e.stopPropagation(); openExternal(ext.getAttribute('data-ext')); return; }
        const actor = e.target.closest('[data-actor]');
        if (actor) { e.preventDefault(); e.stopPropagation(); dlg.close(); window.app.views.main.router.navigate('/actor/' + encodeURIComponent(actor.getAttribute('data-actor')) + '/'); }
    });
    dlg.open();
    enhance(dlg.el);
}

/** Comparte el enlace de la ficha: hoja de compartir del sistema o, si no hay, al portapapeles. */
async function share(info, kind) {
    // El enlace pasa por la página de compartir (/s) para que el chat enseñe la carátula y el título; esa página lleva además la
    // ruta de la ficha para abrirla directamente.
    const name = stripCountry(info.name || '');
    let url;
    try {
        const internal = publicUrl((kind === 'vod' ? '/movie/' : '/series/') + info.id + '/').replace(/\/$/, '');
        url = previewLink(kind, { title: name, year: info.year, internal });
    } catch (e) {
        url = previewLink(kind, { title: name, year: info.year }); // sin la ruta interna, el enlace sigue sirviendo para compartir
    }
    return shareContent({ title: (info.name || name) + ' · ' + t('app.name'), url });
}

/** Progreso de lo que está a medias: barra y «43:27 / 2:28:00». */
function progressHtml(p, label = '') {
    if (!p || p.finished || !(p.pct > 0) || !p.duration) return '';
    return `<div class="detail-progress"><span class="detail-progress-bar"><i style="width:${Math.min(100, p.pct)}%"></i></span><span class="detail-progress-text">${label ? escapeHtml(label) + ' · ' : ''}${formatDuration(p.position)} / ${formatDuration(p.duration)}</span></div>`;
}

function heartButton(kind, id) {
    const fav = catalog.isFavorite(kind, id);
    return `<button class="btn btn-icon btn-round hero-secondary ${fav ? 'is-active' : ''}" data-fav="${kind}:${id}" data-fav-heart aria-label="${fav ? t('common.favoriteRemove') : t('common.favoriteAdd')}"><span class="mi">${fav ? 'favorite' : 'favorite_border'}</span></button>`;
}

/** Botón «Tráiler» en la cabecera: solo si el ajuste lo pide y hay uno que se pueda ver en este aparato. */
function trailerButton(info) {
    if (!settings.get('trailerButton')) return '';
    if (!trailerId(info) && !externalTrailerUrl(info)) return '';
    return `<button class="btn btn-lg hero-secondary" data-trailer><span class="mi">smart_display</span><span class="trailer-label">${t('movies.trailer')}</span></button>`;
}

function moreButton(cls) {
    return `<button class="${cls}" data-more aria-haspopup="true" aria-label="${t('common.moreOptions')}"><span class="mi">more_horiz</span></button>`;
}

function youtubeKey(info) {
    if (!info.youtube) return '';
    return String(info.youtube).replace(/.*[?&]v=|.*youtu\.be\//, '').split('&')[0];
}

/** Tráiler que se puede reproducir en la propia cabecera: navegador y app de Windows (que añade el Referer que YouTube exige); no en la tele ni en Android, donde YouTube no deja incrustar. */
function trailerId(info) {
    if (device.isTV || device.isCordova) return '';
    return youtubeKey(info);
}

/** En Android el tráiler se abre en YouTube / el navegador, que sí lo reproducen. */
function youtubeWatchUrl(info) {
    const key = youtubeKey(info);
    return key ? 'https://www.youtube.com/watch?v=' + encodeURIComponent(key) : '';
}

function externalTrailerUrl(info) {
    if (device.isTV || !device.isCordova) return '';
    return youtubeWatchUrl(info);
}

/** País para «dónde verla»: el de la configuración del aparato si lo trae (es-MX → MX) o el del idioma elegido. */
function watchRegion(watch) {
    const nav = String((typeof navigator !== 'undefined' && navigator.language) || '');
    const m = /[-_]([A-Za-z]{2})$/.exec(nav);
    const byLang = { es: 'ES', en: 'US', fr: 'FR', de: 'DE', it: 'IT', pt: 'PT' };
    const cands = [m && m[1].toUpperCase(), byLang[i18n.lang], 'ES', 'US'].filter(Boolean);
    return cands.find((c) => watch && watch[c]) || '';
}

/* Buscadores de las plataformas con el título ya escrito: TMDB no da enlaces a la ficha de cada una (eso no es gratuito),
   pero la búsqueda de la propia plataforma, con la app instalada, abre la app justo en ese título. */
const PLATFORM_SEARCH = [
    [/netflix/i, 'https://www.netflix.com/search?q='],
    [/prime video|amazon/i, 'https://www.primevideo.com/search?phrase='],
    [/disney/i, 'https://www.disneyplus.com/search?q='],
    [/^max$|hbo/i, 'https://play.max.com/search?q='],
    [/apple tv/i, 'https://tv.apple.com/search?term='],
    [/paramount/i, 'https://www.paramountplus.com/search/?q='],
    [/crunchyroll/i, 'https://www.crunchyroll.com/search?q='],
    [/filmin/i, 'https://www.filmin.es/buscador?q='],
    [/google play/i, 'https://play.google.com/store/search?c=movies&q='],
    [/youtube/i, 'https://www.youtube.com/results?search_query='],
    // Solo funcionan en España (su web es por país); en otros países se cae a la página de TMDB.
    [/movistar/i, 'https://www.movistarplus.es/buscador?q=', 'ES'],
    [/rakuten/i, 'https://www.rakuten.tv/es/search?q=', 'ES']
];

/** Título limpio para buscar: sin año, país ni etiquetas de calidad que el proveedor añade al nombre. */
function watchTitle(info) {
    const t0 = String(info.tmdbTitle || info.name || '');
    return t0.replace(/^[A-Z]{2,3}\s*[|:\-]\s*/, '').replace(/\s*[\[(][^\])]*[\])]/g, '').replace(/\s*[-–]\s*(19|20)\d{2}\s*$/, '').replace(/\b(4k|uhd|hdr|1080p|720p|hd|fhd)\b/ig, '').replace(/\s+/g, ' ').trim();
}

function platformUrl(name, title, fallback, country) {
    const hit = PLATFORM_SEARCH.find(([re]) => re.test(name || ''));
    return hit && title && (!hit[2] || hit[2] === country) ? hit[1] + encodeURIComponent(title) : fallback;
}

/** «Ver en»: plataformas con suscripción donde está disponible, y «Alquilar o comprar»; al pulsar se abre su página de disponibilidad. */
function watchHtml(info, { subscription = false } = {}) {
    const cc = watchRegion(info.watch);
    const w = cc && info.watch[cc];
    if (!w) return [];
    const title = watchTitle(info);
    const chip = (p) => `<button type="button" class="cast-chip watch-chip" ${platformUrl(p.name, title, w.link, cc) ? `data-ext="${escapeHtml(platformUrl(p.name, title, w.link, cc))}"` : 'disabled'} title="${escapeHtml(p.name)}">${p.logo ? `<img data-src="${escapeHtml(imageUrl(p.logo))}" alt=""/>` : ''}${escapeHtml(p.name)}</button>`;
    const rows = [];
    if (w.flatrate.length) rows.push(`<div class="detail-fact"><b>${t('detail.watchOn')}</b><span class="cast-list is-expanded" data-nav-row data-nav-reach>${w.flatrate.map(chip).join('')}</span></div>`);
    if (subscription) return rows;
    const paid = w.rent.concat(w.buy.filter((p) => !w.rent.some((r) => r.id === p.id)));
    if (paid.length) rows.push(`<div class="detail-fact"><b>${t('detail.rentBuy')}</b><span class="cast-list is-expanded" data-nav-row>${paid.map(chip).join('')}</span></div>`);
    if (rows.length) rows.push(`<div class="detail-fact detail-credit"><small>${t('detail.justwatch')}</small></div>`);
    return rows;
}

function openExternalTrailer(url) {
    openExternal(url);
}

/** Entrada de menú «Tráiler» (en la cabecera o fuera, según dónde se pueda reproducir). */
function trailerItem(info, root, preview) {
    if (trailerId(info)) return { icon: 'smart_display', label: t('movies.trailer'), onClick: () => showTrailer(root, preview, info) };
    const url = externalTrailerUrl(info);
    return url ? { icon: 'smart_display', label: t('movies.trailer'), onClick: () => openExternalTrailer(url) } : null;
}

/** Reproduce el tráiler en la propia cabecera, con sonido, y sube a verla. */
function showTrailer(root, preview, info) {
    // App de Windows: si YouTube no deja incrustarlo (algunos vídeos lo prohíben fuera de youtube.com), se abre en el navegador.
    preview.onError = device.isElectron && info ? () => openExternalTrailer(youtubeWatchUrl(info)) : null;
    if (!preview.playTrailer({ sound: true })) return;
    const content = root.querySelector('.page-content');
    if (content && content.scrollTo) content.scrollTo({ top: 0, behavior: 'smooth' });
}

/** Aviso que sustituye a los botones de reproducción cuando la clasificación supera la edad del espectador. */
function ageBlockHtml(info) {
    const a = parseAge(info.age);
    const viewer = catalog.viewer ? catalog.viewer.name : '';
    return `<div class="detail-warn is-block"><span class="mi">block</span><span><b>${escapeHtml(t('age.blockedTitle'))}</b><br>${escapeHtml(t('age.blockedText', { label: a.label, viewer, max: catalog.maxAge }))}</span></div>`;
}

/** «Más información»: solo el símbolo, en una esquina de la cabecera (en móvil entre los iconos de arriba; en escritorio y tele, arriba a la derecha). */
function infoButton(cls) {
    return `<button type="button" class="${cls}" data-info aria-label="${escapeHtml(t('detail.moreInfo'))}" title="${escapeHtml(t('detail.moreInfo'))}"><span class="mi">info</span></button>`;
}

function cornerFav(kind, id) {
    const fav = catalog.isFavorite(kind, id);
    return `<button class="btn btn-icon hero-corner-btn ${fav ? 'is-active' : ''}" data-fav="${kind}:${id}" aria-label="${fav ? t('common.favoriteRemove') : t('common.favoriteAdd')}"><span class="mi">${fav ? 'check' : 'add'}</span></button>`;
}

/* Con TMDB configurado, la carátula buena llega un instante después de abrir la ficha. Mientras tanto no se pinta la del
   proveedor (se vería un salto de una imagen a otra): fondo liso y, al llegar, la imagen definitiva se funde. */
function artPending(kind, id) {
    const have = catalog.peekInfo(kind, id);
    return !!catalog.enricher && !(have && have.enriched === 4);
}

/* Toda la ficha va dentro de la cabecera, sobre la imagen: título, datos, progreso, acciones,
   sinopsis y reparto. En escritorio y TV las acciones secundarias son el corazón y el menú ⋯;
   en móvil van como iconos en la esquina superior y la información queda bajo la imagen. */
function heroFor(info, kind, { actions = '', corner = '', progress = '', body = '', art = true } = {}) {
    const blockedHero = catalog.ageBlocked(info);
    const bg = art ? info.backdrop || info.poster : '';
    const meta = [];
    if (info.year) meta.push(`<span>${escapeHtml(info.year)}</span>`);
    if (info.rating) meta.push(`<span class="rating-star"><span class="mi">star</span>${Number(info.rating).toFixed(1)}</span>`);
    if (info.durationSecs) meta.push(`<span>${formatMinutes(info.durationSecs)}</span>`);
    else if (info.duration && kind === 'vod') meta.push(`<span>${escapeHtml(info.duration)}</span>`);
    if (info.episodeRunTime) meta.push(`<span>${escapeHtml(info.episodeRunTime)} ${t('common.minutes')}</span>`);
    const age = parseAge(info.age);
    if (age.label) meta.push(`<span class="tag tag-age ${catalog.ageBlocked(info) ? 'is-blocked' : ''}">${escapeHtml(age.label)}</span>`);
    const genres = genreList(info.genre);
    if (genres.length) meta.push(`<span>${escapeHtml(genres.slice(0, 2).join(', '))}</span>`);
    return `<section class="hero detail-hero is-${kind}">
        ${backButton(kind)}
        ${blockedHero ? '' : infoButton('btn btn-icon hero-info-desk')}
        <div class="hero-bg${art === 'fade' ? ' is-art-in' : ''}"${bg ? ` style="background-image:url('${escapeHtml(imageUrl(bg))}')"` : ''}></div>
        ${corner ? `<div class="hero-corner">${corner}</div>` : ''}
        <div class="hero-content">
            <div class="detail-head">
                <div class="hero-kicker"><span class="mi">${kind === 'vod' ? 'movie' : 'tv'}</span>${kind === 'vod' ? t('detail.kindMovie') : t('detail.kindSeries')}</div>
                <h1 class="hero-title">${escapeHtml(stripCountry(info.name))}</h1>
                <div class="hero-meta">${meta.join('')}</div>
                ${progress}
                <div class="hero-actions" data-actions>${actions}</div>
            </div>
            ${body ? `<div class="detail-info">${body}</div>` : ''}
        </div>
    </section>`;
}

function bodyHtml(info) {
    return `${compatWarning(info)}${info.plot ? `<p class="detail-plot" data-plot-toggle tabindex="0" data-nav-row data-nav-reach>${escapeHtml(info.plot)}</p>` : ''}${info.plot ? plotCreditHtml(info) : ''}${facts(info)}`;
}

/* Pasar de ficha deslizando el dedo: al abrir una ficha desde una lista se recuerda qué otros títulos había alrededor
   (los pintados en ese momento) y en la ficha un gesto horizontal lleva al siguiente (hacia la izquierda) o al anterior
   (hacia la derecha), sustituyendo la ficha en el historial para que «atrás» vuelva a la lista. Solo en pantallas táctiles. */
let siblings = { kind: '', ids: [] };

export function setDetailSiblings(kind, ids) {
    siblings = { kind, ids: Array.from(new Set(ids)).slice(0, 500) };
}

function bindDetailSwipe(root, kind, id) {
    if (!device.hasTouch || device.isTV) return () => {};
    const content = root.querySelector('.page-content') || root;
    let start = null;
    const onStart = (e) => {
        const tch = e.touches && e.touches[0];
        // No dentro de lo que ya se desplaza de lado (reparto, similares, temporadas) ni desde los bordes (gesto «atrás» de Android).
        if (!tch || e.touches.length > 1 || e.target.closest('.row-scroller, .cast-list, .chips, .seasons, input, textarea, [data-no-swipe]') || tch.clientX < 28 || tch.clientX > window.innerWidth - 28) { start = null; return; }
        start = { x: tch.clientX, y: tch.clientY, t: Date.now() };
    };
    const onEnd = (e) => {
        if (!start) return;
        const tch = e.changedTouches && e.changedTouches[0];
        const s = start;
        start = null;
        if (!tch) return;
        const dx = tch.clientX - s.x;
        const dy = tch.clientY - s.y;
        if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.8 || Date.now() - s.t > 800) return;
        if (siblings.kind !== kind) return;
        const i = siblings.ids.indexOf(String(id));
        if (i === -1) return;
        const next = siblings.ids[i + (dx < 0 ? 1 : -1)];
        if (!next || !catalog.get(kind, next)) return;
        window.app.views.main.router.navigate((kind === 'vod' ? '/movie/' : '/series/') + next + '/', { reloadCurrent: true, animate: true });
    };
    const onCancel = () => { start = null; };
    content.addEventListener('touchstart', onStart, { passive: true });
    content.addEventListener('touchend', onEnd, { passive: true });
    content.addEventListener('touchcancel', onCancel, { passive: true });
    return () => { content.removeEventListener('touchstart', onStart); content.removeEventListener('touchend', onEnd); content.removeEventListener('touchcancel', onCancel); };
}

export const movieDetailPage = {
    path: '/movie/:id/',
    nav: 'movies',

    render(route) {
        const item = catalog.get('vod', route.params.id);
        return `<div class="page no-nav" data-name="movie-detail">
            <div class="page-content">
                <div data-detail>${item ? heroFor(item, 'vod', { actions: '<div class="spinner"></div>', art: !artPending('vod', route.params.id) }) : empty({ icon: 'error_outline', title: t('common.errorLoad') })}</div>
            </div>
        </div>`;
    },

    async mount(page, route) {
        const root = page.el;
        const box = root.querySelector('[data-detail]');
        const id = route.params.id;
        let info = catalog.get('vod', id);
        if (!info) return () => {};
        const offSwipe = bindDetailSwipe(root, 'vod', id);
        // Carátula: con TMDB configurado se espera a la ficha completa para no saltar de una imagen a otra (máximo 2,5 s).
        let art = !artPending('vod', id);
        const revealArt = () => { if (art !== false) return false; art = 'fade'; paint(); art = true; return true; };
        const artTimer = setTimeout(() => { if (root.isConnected) revealArt(); }, 2500);

        const paint = () => (device.isTV ? window.IPTV.tvnav.preserve(() => paintNow(), root) : paintNow());
        const paintNow = () => {
            const progress = catalog.getProgress('vod', id);
            const canResume = progress && !progress.finished && progress.pct > 0;
            const blocked = catalog.ageBlocked(info);
            const actions = `
                ${canResume
                    ? `<button class="btn btn-primary btn-lg btn-progress" data-play="vod:${id}" data-resume-pos="${progress.position}" data-nav-default><div class="btn-progress-bar" style="width:${progress.pct}%"></div><span class="mi">play_arrow</span>${t('common.resume')} · ${formatDuration(progress.position)}</button>
                       <button class="btn btn-lg hero-secondary" data-play="vod:${id}" data-resume-pos="0"><span class="mi">replay</span>${t('common.restart')}</button>`
                    : `<button class="btn btn-primary btn-lg" data-play="vod:${id}" data-resume-pos="0" data-nav-default><span class="mi">play_arrow</span>${t('common.play')}</button>`}
                ${trailerButton(info)}
                ${heartButton('vod', id)}
                ${moreButton('btn btn-icon btn-round hero-secondary')}`;
            const corner = `${canResume ? `<button class="btn btn-icon hero-corner-btn" data-play="vod:${id}" data-resume-pos="0" aria-label="${t('common.restart')}"><span class="mi">replay</span></button>` : ''}${infoButton('btn btn-icon hero-corner-btn')}${cornerFav('vod', id)}${offlineButton('vod', id, { cls: 'btn btn-icon hero-corner-btn', icon: true })}${moreButton('btn btn-icon hero-corner-btn')}`;
            const byTaste = settings.get('similarByTaste') !== false;
            const similar = byTaste ? catalog.similarTo('vod', catalog.get('vod', id) || info, 20) : catalog.byCategory('vod', info.categoryId).filter((m) => m.id !== id).slice(0, 20);
            box.innerHTML = `
                ${heroFor(info, 'vod', blocked
                    ? { actions: ageBlockHtml(info), corner: cornerFav('vod', id), body: bodyHtml(info), art }
                    : { actions, corner, progress: canResume ? progressHtml(progress) : '', body: bodyHtml(info), art })}
                ${row({ id: 'similar', title: byTaste ? t('detail.similar') : (catalog.category('vod', info.categoryId) || { name: t('nav.movies') }).name, items: similar.map(movieCard) })}`;
            enhance(box);
            preview.attach();
        };
        const menuItems = () => {
            const fav = catalog.isFavorite('vod', id);
            const rec = offline.get(offlineKey('vod', id));
            const state = !rec ? t('offline.download') : rec.status === 'done' ? t('offline.downloaded') : rec.status === 'downloading' ? t('offline.downloading') + ' · ' + progressText(rec) : t('offline.title');
            return [
                { icon: fav ? 'playlist_remove' : 'add', label: fav ? t('common.favoriteRemove') : t('common.favoriteAdd'), onClick: () => window.IPTV.actions.toggleFavorite('vod', id) },
                { icon: catalog.isWatched('vod', id) ? 'visibility_off' : 'visibility', label: catalog.isWatched('vod', id) ? t('watched.markUnwatched') : t('watched.markWatched'), onClick: async () => { const on = !catalog.isWatched('vod', id); await catalog.setWatched('vod', id, on); notify.toast(t(on ? 'watched.toastWatched' : 'watched.toastUnwatched'), { icon: on ? 'visibility' : 'visibility_off', duration: 1800 }); } },
                offline.supported && !catalog.ageBlocked(info) ? { icon: rec && rec.status === 'done' ? 'download_done' : 'download', label: state, onClick: () => offlineAction('vod', id) } : null,
                catalog.ageBlocked(info) ? null : { icon: 'speed', label: t('speed.button'), onClick: () => { preview.stop(); openSpeedTest(info).catch(() => {}); } },
                trailerItem(info, root, preview),
                { icon: 'share', label: t('detail.share'), onClick: () => share(info, 'vod') },
                { icon: 'info', label: t('detail.info'), onClick: () => openInfo(info) },
                itemEdits.canEdit(catalog.get('vod', id)) ? { icon: 'edit', label: t('edit.menu'), onClick: () => { preview.stop(); openEditItem({ kind: 'vod', item: catalog.get('vod', id), onDone: () => window.app.views.main.router.refreshPage() }); } } : null,
                catalog.canFixMatch(catalog.get('vod', id)) ? { icon: 'manage_search', label: t('match.menu'), onClick: () => openMatch({ kind: 'vod', item: catalog.get('vod', id), onDone: () => window.app.views.main.router.refreshPage() }) } : null,
                // Solo las películas que ofrece Filoteca («Cine clásico»): de las fuentes del usuario no puede retirar nada.
                /(^|~)ia-/.test(String(id)) ? { icon: 'flag', label: t('report.title'), onClick: () => openReport({ target: { type: 'archive', id: String(id).replace(/^.*~/, ''), title: info.name } }) } : null
            ];
        };
        const preview = new HeroPreview(
            () => box.querySelector('.hero'),
            () => {
                const progress = catalog.getProgress('vod', id);
                const startAt = progress && !progress.finished && progress.position > 0 ? progress.position : previewStart(info.durationSecs);
                return { url: catalog.vodUrl(info), startAt };
            },
            () => trailerId(info)
        );

        paint();
        catalog.pullProgress().catch(() => {}); // progreso de otros dispositivos; repinta vía 'progress'
        info = (await catalog.vodInfo(id)) || info;
        clearTimeout(artTimer);
        if (!root.isConnected) return () => {};
        if (!revealArt()) paint();
        if (device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(root), 80);
        const offPreview = catalog.ageBlocked(info) ? (preview.destroy(), () => {}) : bindPreview(root, preview);

        const onClick = (e) => {
            const sp = e.target.closest('[data-spotify]');
            if (sp) { e.preventDefault(); playSoundtrack(info, { fallbackUrl: sp.getAttribute('data-url') || '' }); return; }
            const ext = e.target.closest('[data-ext]');
            if (ext) { e.preventDefault(); openExternal(ext.getAttribute('data-ext')); return; }
            const more = e.target.closest('[data-more]');
            if (more) {
                e.preventDefault();
                openMenu(more, menuItems());
                return;
            }
            if (e.target.closest('[data-trailer]')) {
                e.preventDefault();
                const item = trailerItem(info, root, preview);
                if (item) item.onClick();
                return;
            }
            const plot = e.target.closest('[data-plot-toggle]');
            if (plot) { plot.classList.toggle('is-open'); return; }
            if (e.target.closest('[data-info]')) openInfo(info);
        };
        root.addEventListener('click', onClick);
        const off = catalog.on('progress', paint);
        const offFav = catalog.on('favorites', paint);
        return () => {
            clearTimeout(artTimer);
            off();
            offFav();
            offPreview();
            offSwipe();
        };
    }
};

export const seriesDetailPage = {
    path: '/series/:id/',
    nav: 'series',

    render(route) {
        const item = catalog.get('series', route.params.id);
        return `<div class="page no-nav" data-name="series-detail">
            <div class="page-content">
                <div data-detail>${item ? heroFor(item, 'series', { actions: '<div class="spinner"></div>', art: !artPending('series', route.params.id) }) : empty({ icon: 'error_outline', title: t('common.errorLoad') })}</div>
            </div>
        </div>`;
    },

    async mount(page, route) {
        const root = page.el;
        const box = root.querySelector('[data-detail]');
        const id = route.params.id;
        let info = catalog.get('series', id);
        if (!info) return () => {};
        const offSwipe = bindDetailSwipe(root, 'series', id);
        // Carátula: con TMDB configurado se espera a la ficha completa para no saltar de una imagen a otra (máximo 2,5 s).
        let art = !artPending('series', id);
        const revealArt = () => { if (art !== false) return false; art = 'fade'; paint(); art = true; return true; };
        const artTimer = setTimeout(() => { if (root.isConnected) revealArt(); }, 2500);
        catalog.pullProgress().catch(() => {});
        const state = { season: null };

        const paint = (opts) => (device.isTV ? window.IPTV.tvnav.preserve(() => paintNow(opts), root) : paintNow(opts));
        const paintNow = ({ episodesOnly = false } = {}) => {
            const seasons = info.seasons || [];
            const progress = catalog.seriesProgress(id);
            if (state.season == null) {
                state.season = progress ? progress.season : seasons.length ? seasons[0].number : 1;
            }
            let primary;
            if (progress && !progress.finished) {
                primary = `<button class="btn btn-primary btn-lg btn-progress" data-play-episode="${id}:${progress.season}:${progress.episode}" data-resume-pos="${progress.position}" data-nav-default><div class="btn-progress-bar" style="width:${progress.pct}%"></div><span class="mi">play_arrow</span>${t('series.continue', { season: progress.season, episode: progress.episode })}</button>`;
            } else if (progress && progress.finished) {
                const nextEp = catalog.nextEpisode(info, { id: '', season: progress.season, num: progress.episode });
                const ep = nextEp || (seasons[0] && seasons[0].episodes[0]);
                primary = ep ? `<button class="btn btn-primary btn-lg" data-play-episode="${id}:${ep.season}:${ep.num}" data-resume-pos="0" data-nav-default><span class="mi">play_arrow</span>T${ep.season} E${ep.num}</button>` : '';
            } else {
                const ep = seasons[0] && seasons[0].episodes[0];
                primary = ep ? `<button class="btn btn-primary btn-lg" data-play-episode="${id}:${ep.season}:${ep.num}" data-resume-pos="0" data-nav-default><span class="mi">play_arrow</span>${t('series.playFirst')}</button>` : '';
            }
            const actions = `${primary}${trailerButton(info)}${heartButton('series', id)}${moreButton('btn btn-icon btn-round hero-secondary')}`;
            const corner = infoButton('btn btn-icon hero-corner-btn') + cornerFav('series', id) + moreButton('btn btn-icon hero-corner-btn');
            const blocked = catalog.ageBlocked(info);
            const current = progress && !progress.finished ? progressHtml(progress, `T${progress.season} E${progress.episode}`) : '';

            // Serie «vista»: todos sus episodios vistos (se guarda para la insignia de la carátula y el filtro «No vistas»).
            const everyEp = seasons.reduce((all, s) => all.concat(s.episodes), []);
            if (everyEp.length) {
                const complete = everyEp.every((e) => catalog.isWatched('episode', e.id));
                if (complete !== catalog.isWatched('seriesdone', id)) catalog.setWatched('seriesdone', id, complete).catch(() => {});
            }

            const season = seasons.find((s) => s.number === state.season) || seasons[0];
            const episodes = season ? season.episodes : [];
            const seasonChips = seasons.length > 1 ? `<div class="chips" data-nav-row data-nav-reach>${seasons.map((s) => `<button class="chip ${season && s.number === season.number ? 'is-active' : ''}" data-season="${s.number}">${t('common.season')} ${s.number}</button>`).join('')}</div>` : '';
            // Ajuste «Episodios»: tarjetas con imagen (como Prime) o lista; en automático, tarjetas en pantallas grandes.
            const epMode = settings.get('episodeView');
            const asCards = epMode === 'cards' || (epMode !== 'list' && window.innerWidth >= 900);
            const epFallback = info.backdrop || info.poster || '';
            const epHtml = episodes.map((ep) => {
                const p = catalog.getProgress('episode', ep.id);
                const current = progress && progress.season === ep.season && progress.episode === ep.num;
                const seen = !!(p && p.finished);
                return `<a class="episode ${current ? 'is-current' : ''} ${seen ? 'is-watched' : ''}" href="#" data-play-episode="${id}:${ep.season}:${ep.num}" data-resume-pos="${p && !p.finished ? p.position : 0}">
                    <div class="episode-num">${ep.num}</div>
                    <div class="episode-thumb ${!ep.thumb && asCards && epFallback ? 'is-fallback' : ''}">${ep.thumb ? `<img data-src="${escapeHtml(imageUrl(ep.thumb))}" alt="" loading="lazy"/>` : asCards && epFallback ? `<img data-src="${escapeHtml(imageUrl(epFallback))}" alt="" loading="lazy"/><span class="mi">play_circle</span>` : '<span class="mi">play_circle</span>'}${asCards ? '<span class="episode-play"><span class="mi">play_arrow</span></span>' : ''}${p && p.pct > 0 ? `<div class="card-progress"><i style="width:${p.finished ? 100 : p.pct}%"></i></div>` : ''}</div>
                    <div class="episode-body">
                        <div class="episode-title"><span><span class="episode-ord">${ep.num}. </span>${escapeHtml(ep.title)}</span><span>${ep.durationSecs ? formatMinutes(ep.durationSecs) : escapeHtml(ep.duration || '')}</span></div>
                        ${ep.plot ? `<div class="episode-plot">${escapeHtml(ep.plot)}</div>` : ''}
                    </div>
                    <div class="episode-actions">
                    <button type="button" class="btn btn-icon episode-watch ${seen ? 'is-on' : ''}" tabindex="-1" data-watch-ep="${ep.id}" aria-label="${escapeHtml(seen ? t('watched.markUnwatched') : t('watched.markWatched'))}" title="${escapeHtml(seen ? t('watched.markUnwatched') : t('watched.markWatched'))}"><span class="mi">${seen ? 'check_circle' : 'radio_button_unchecked'}</span></button>
                    ${offlineButton('episode', ep.id, { cls: 'btn btn-icon episode-dl', icon: true, ctx: { series: id, season: ep.season, num: ep.num } }).replace('<button ', '<button tabindex="-1" ')}
                    </div>
                </a>`;
            }).join('');
            const byTaste = settings.get('similarByTaste') !== false;
            const similar = byTaste ? catalog.similarTo('series', catalog.get('series', id) || info, 20) : catalog.byCategory('series', info.categoryId).filter((s) => s.id !== id).slice(0, 20);

            const sectionHtml = blocked ? '' : `<section class="section" data-episodes>
                    <div class="section-head"><h2 class="section-title">${t('common.episodes')}</h2>${episodes.length ? (() => { const all = episodes.every((e) => catalog.isWatched('episode', e.id)); return `<button type="button" class="section-link" data-watch-season="${season ? season.number : ''}" aria-label="${escapeHtml(t(all ? 'watched.markSeasonUnwatched' : 'watched.markSeasonWatched'))}"><span class="watch-season-label">${t(all ? 'watched.markSeasonUnwatched' : 'watched.markSeasonWatched')}</span><span class="mi">${all ? 'visibility_off' : 'visibility'}</span></button>`; })() : ''}</div>
                    ${seasonChips}
                    <div class="episodes ${asCards ? 'is-cards' : ''}">${epHtml || `<div class="empty"><p>${info.seasons ? t('common.noResults') : t('common.loading')}</p></div>`}</div>
                </section>`;
            // Cambiar de temporada solo repinta los episodios: la cabecera (y su tráiler) no se toca.
            const current$ = box.querySelector('[data-episodes]');
            if (episodesOnly && current$ && sectionHtml) {
                current$.outerHTML = sectionHtml;
                enhance(box);
                return;
            }
            box.innerHTML = `
                ${heroFor(info, 'series', blocked
                    ? { actions: ageBlockHtml(info), corner: cornerFav('series', id), body: bodyHtml(info), art }
                    : { actions, corner, progress: current, body: bodyHtml(info), art })}
                ${sectionHtml}
                ${row({ id: 'similar', title: byTaste ? t('detail.similar') : (catalog.category('series', info.categoryId) || { name: t('nav.series') }).name, items: similar.map(seriesCard) })}`;
            enhance(box);
            preview.attach();
        };
        const preview = new HeroPreview(
            () => box.querySelector('.hero'),
            () => {
                const seasons = info.seasons || [];
                const progress = catalog.seriesProgress(id);
                let ep = null;
                let startAt = 0;
                if (progress && !progress.finished) {
                    ep = catalog.findEpisode(info, progress.season, progress.episode);
                    startAt = progress.position || 0;
                }
                if (!ep) {
                    ep = seasons[0] && seasons[0].episodes[0];
                    startAt = ep ? previewStart(ep.durationSecs) : 0;
                }
                return ep ? { url: catalog.episodeUrl(ep), startAt } : null;
            },
            () => trailerId(info)
        );

        paint();
        info = (await catalog.seriesInfo(id)) || info;
        clearTimeout(artTimer);
        if (!root.isConnected) return () => {};
        if (!revealArt()) paint();
        // Imágenes, sinopsis y duración de los episodios desde TMDB (la temporada a la vista; las demás al elegirlas).
        const fillSeasonArt = () => catalog.fillSeason(info, state.season).then((changed) => { if (changed && root.isConnected) paint({ episodesOnly: true }); }).catch(() => {});
        fillSeasonArt();
        if (device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(root), 80);
        const offPreview = catalog.ageBlocked(info) ? (preview.destroy(), () => {}) : bindPreview(root, preview);

        const onClick = async (e) => {
            // Marcar un episodio o la temporada como vistos: no debe iniciar la reproducción del episodio que lo contiene.
            const watchEp = e.target.closest('[data-watch-ep]');
            if (watchEp) {
                e.preventDefault();
                e.stopPropagation();
                const epId = watchEp.getAttribute('data-watch-ep');
                const ep = (info.seasons || []).reduce((found, s) => found || s.episodes.find((x) => String(x.id) === epId), null);
                if (ep) await catalog.setEpisodesWatched(id, [ep], !catalog.isWatched('episode', ep.id), info.poster || '');
                return;
            }
            const watchSeason = e.target.closest('[data-watch-season]');
            if (watchSeason) {
                e.preventDefault();
                e.stopPropagation();
                const sn = (info.seasons || []).find((s) => s.number === Number(watchSeason.getAttribute('data-watch-season')));
                if (sn) {
                    const on = !sn.episodes.every((x) => catalog.isWatched('episode', x.id));
                    await catalog.setEpisodesWatched(id, sn.episodes, on, info.poster || '');
                    notify.toast(t(on ? 'watched.toastWatched' : 'watched.toastUnwatched'), { icon: on ? 'visibility' : 'visibility_off', duration: 1800 });
                }
                return;
            }
            const chip = e.target.closest('.chip[data-season]'); // solo las pestañas de temporada (los botones de descarga de los episodios también llevan data-season)
            if (chip) {
                state.season = Number(chip.getAttribute('data-season'));
                const top = root.querySelector('.page-content').scrollTop;
                paint({ episodesOnly: true });
                fillSeasonArt();
                root.querySelector('.page-content').scrollTop = top;
                // La lista se redibuja y la pestaña pulsada ya no existe: con el mando, el foco vuelve a la pestaña nueva de esa temporada
                // (si no, la siguiente flecha saltaba a la cabecera y no se llegaba a los episodios).
                if (device.isTV) {
                    const again = root.querySelector('.chip[data-season="' + state.season + '"]');
                    if (again) window.IPTV.tvnav.focus(again, { scroll: false });
                }
                return;
            }
            const sp = e.target.closest('[data-spotify]');
            if (sp) { e.preventDefault(); playSoundtrack(info, { fallbackUrl: sp.getAttribute('data-url') || '' }); return; }
            const ext = e.target.closest('[data-ext]');
            if (ext) { e.preventDefault(); openExternal(ext.getAttribute('data-ext')); return; }
            const more = e.target.closest('[data-more]');
            if (more) {
                e.preventDefault();
                const fav = catalog.isFavorite('series', id);
                openMenu(more, [
                    { icon: fav ? 'playlist_remove' : 'add', label: fav ? t('common.favoriteRemove') : t('common.favoriteAdd'), onClick: () => window.IPTV.actions.toggleFavorite('series', id) },
                    trailerItem(info, root, preview),
                    { icon: 'share', label: t('detail.share'), onClick: () => share(info, 'series') },
                    { icon: 'info', label: t('detail.info'), onClick: () => openInfo(info) },
                    itemEdits.canEdit(catalog.get('series', id)) ? { icon: 'edit', label: t('edit.menu'), onClick: () => openEditItem({ kind: 'series', item: catalog.get('series', id), onDone: () => window.app.views.main.router.refreshPage() }) } : null,
                    catalog.canFixMatch(catalog.get('series', id)) ? { icon: 'manage_search', label: t('match.menu'), onClick: () => openMatch({ kind: 'series', item: catalog.get('series', id), onDone: () => window.app.views.main.router.refreshPage() }) } : null
                ]);
                return;
            }
            if (e.target.closest('[data-trailer]')) {
                e.preventDefault();
                const item = trailerItem(info, root, preview);
                if (item) item.onClick();
                return;
            }
            const plot = e.target.closest('[data-plot-toggle]');
            if (plot) { plot.classList.toggle('is-open'); return; }
            if (e.target.closest('[data-info]')) openInfo(info);
        };
        root.addEventListener('click', onClick);
        const repaint = () => {
            const top = root.querySelector('.page-content').scrollTop;
            paint();
            root.querySelector('.page-content').scrollTop = top;
        };
        const off = catalog.on('progress', repaint);
        const offFav = catalog.on('favorites', repaint);
        return () => {
            clearTimeout(artTimer);
            off();
            offFav();
            offPreview();
            offSwipe();
        };
    }
};
